/**
 * Language, appearance and mode — one small row at the foot of the rail.
 *
 * These three were scattered: language and theme lived in the top bar of every
 * page, and the mode switch did not exist. They belong together and they belong
 * here, because they share a property nothing else in the navigation has —
 * they change how the WHOLE app behaves, not which part of it you are looking
 * at. Mixing them into the page chrome put app-level settings on the same shelf
 * as page-level actions.
 *
 * They are also all things you set once and rarely touch, which is why they get
 * icons at the bottom rather than labelled rows in the navigation.
 *
 * COLLAPSED RAIL. At icon width there is no room for three buttons side by
 * side, so the row stacks. It is never hidden: the language switch is the one
 * control a bilingual office reaches for constantly, and hiding it behind an
 * expand would be the single most annoying thing in the product.
 */

import { Languages, Moon, MousePointer2, Sun } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useAppMode } from '@/contexts/AgentModeContext';
import { useSidebar } from '@/components/shadcn/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip';
import { cn } from '@/lib/utils';

function ControlButton({
  label,
  onClick,
  active,
  side,
  children,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  side: 'left' | 'right';
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          aria-pressed={active}
          className={cn(
            'inline-flex size-7 items-center justify-center rounded-md',
            'text-muted-foreground transition-colors',
            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            // The accent is reserved for the one control that is genuinely a
            // state rather than an action: agent mode being ON.
            active && 'bg-sidebar-accent text-sidebar-accent-foreground',
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side={side}>{label}</TooltipContent>
    </Tooltip>
  );
}

export function SidebarControls() {
  const { t, lang, setLang, dir } = useI18n();
  const { resolvedTheme, setTheme } = useTheme();
  const { isAgent, toggle } = useAppMode();
  const { state, isMobile } = useSidebar();

  const collapsed = state === 'collapsed' && !isMobile;
  const side = dir === 'rtl' ? 'left' : 'right';

  return (
    <div
      className={cn(
        'flex items-center gap-0.5',
        // Stacked when the rail is icons-only; a row otherwise.
        collapsed ? 'flex-col' : 'flex-row px-0.5',
      )}
    >
      {/*
        Agent mode sits FIRST and beside the language switch, as asked. The two
        answer the same kind of question — how do I want to work, and in which
        language — and neither belongs in a settings page you have to go find.
      */}
      <ControlButton
        label={`${t('agent_mode')} · ${isAgent ? t('agent_mode_on') : t('agent_mode_off')}`}
        onClick={toggle}
        active={isAgent}
        side={side}
      >
        <MousePointer2 className="size-4" />
      </ControlButton>

      {/*
        One button, not a segmented control. In the rail there is room for an
        icon and the choice is binary, so tapping it swaps — with the CURRENT
        language shown, because a switch that shows its destination and a switch
        that shows its state are indistinguishable at this size and one of them
        is always wrong.
      */}
      <ControlButton
        label={lang === 'ar' ? 'English' : 'العربية'}
        onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
        side={side}
      >
        <span className="relative">
          <Languages className="size-4" />
          <span className="sr-only">{t('language')}</span>
        </span>
      </ControlButton>

      <ControlButton
        label={t('theme')}
        onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
        side={side}
      >
        {resolvedTheme === 'dark' ? <Moon className="size-4" /> : <Sun className="size-4" />}
      </ControlButton>

      {/* The current language, spelled out, when there is room for it. Two
          letters answer "am I in Arabic?" faster than a globe icon can. */}
      {collapsed ? null : (
        <span className="ms-auto pe-1 text-[10.5px] font-medium tracking-wide text-muted-foreground uppercase">
          {lang === 'ar' ? 'ع' : 'EN'}
        </span>
      )}
    </div>
  );
}
